/* eslint-disable max-lines */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Container, Spinner } from 'reactstrap';
import moment from 'moment';
import CommonHeader from '../../../components/Headers/CommonHeader';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import DailyScheduleService from '../../../services/DailySchedule';
import PtoService from '../../../services/Pto';
import HolidaysService from '../../../services/Holidays';
import { DailyScheduleDTO, DailyScheduleEmployeeDTO } from '../../../interfaces/DailyScheduleInterface';
import { HolidaysInterface } from '../../../interfaces/HolidaysInterface';
import WeeklyScheduleGrid from './components/WeeklyScheduleGrid';
import './daily-schedule.scss';

function getWeekMonday(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return moment(d).format('YYYY-MM-DD');
}

const WeeklySchedulePage = () => {
  const { token, currentSchool, user } = useContext(AuthContext) as any;
  const schoolId = currentSchool?.value || user?.employee?.schoolId;

  const [weekStart, setWeekStart] = useState<string>(() => getWeekMonday(new Date()));
  const [schedules, setSchedules] = useState<(DailyScheduleDTO | null)[]>([null, null, null, null, null]);
  const [employees, setEmployees] = useState<DailyScheduleEmployeeDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [approvedPtoUserIds, setApprovedPtoUserIds] = useState<Record<string, string[]>>({});
  const [holidays, setHolidays] = useState<HolidaysInterface[]>([]);

  const weekDates = useMemo(
    () => [0, 1, 2, 3, 4].map((i) => moment(weekStart).add(i, 'days').format('YYYY-MM-DD')),
    [weekStart],
  );

  const weekLabel = `${moment(weekStart).format('MMM D')} – ${moment(weekStart).add(4, 'days').format('MMM D, YYYY')}`;

  const loadWeek = useCallback(async () => {
    if (!token || !schoolId) return;
    setLoading(true);
    try {
      const result = await DailyScheduleService(token).getWeek(schoolId, weekStart);
      setSchedules((result?.data as (DailyScheduleDTO | null)[]) || [null, null, null, null, null]);
    } catch {
      toast.error('Failed to load weekly schedules.');
    } finally {
      setLoading(false);
    }
  }, [token, schoolId, weekStart]);

  const loadEmployees = useCallback(async () => {
    if (!token || !schoolId) return;
    try {
      const result = await DailyScheduleService(token).listEmployees(schoolId);
      setEmployees(result?.data || []);
    } catch {
      /* silent */
    }
  }, [token, schoolId]);

  const loadPtoAndHolidays = useCallback(async () => {
    if (!token || !schoolId) return;
    try {
      const weekEnd = moment(weekStart).add(4, 'days').format('YYYY-MM-DD');
      const [ptoResult, holidayResult] = await Promise.all([
        PtoService(token).timeOffRequestListByDates(schoolId, moment(weekStart), moment(weekEnd)),
        HolidaysService(token).list(moment(weekStart).year(), schoolId),
      ]);

      const ptoMap: Record<string, string[]> = {};
      const requests = Array.isArray(ptoResult) ? ptoResult : ptoResult?.data || [];
      requests.forEach((req: any) => {
        if (req.status !== 'APPROVE') return;
        const start = moment(req.startDate);
        const end = moment(req.endDate || req.startDate);
        const userId = req.userId;
        const current = start.clone();
        while (current.isSameOrBefore(end, 'day')) {
          const dateKey = current.format('YYYY-MM-DD');
          if (!ptoMap[dateKey]) ptoMap[dateKey] = [];
          if (!ptoMap[dateKey].includes(userId)) ptoMap[dateKey].push(userId);
          current.add(1, 'day');
        }
      });

      setApprovedPtoUserIds(ptoMap);
      setHolidays(Array.isArray(holidayResult) ? holidayResult : holidayResult?.data || []);
    } catch {
      /* silent */
    }
  }, [token, schoolId, weekStart]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    loadPtoAndHolidays();
  }, [loadPtoAndHolidays]);

  const navigateWeek = (direction: -1 | 1) => {
    setWeekStart((prev) =>
      moment(prev)
        .add(direction * 7, 'days')
        .format('YYYY-MM-DD'),
    );
  };

  const handleCopyPreviousWeek = async () => {
    if (!token || !schoolId) return;
    try {
      const result = await DailyScheduleService(token).copyPreviousWeek(schoolId, { weekStart });
      setSchedules((result?.data as (DailyScheduleDTO | null)[]) || [null, null, null, null, null]);
      toast.success('Previous week copied!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No previous week found.');
    }
  };

  const handleSendWeek = async () => {
    const draftSchedules = schedules.filter((s) => s && s.status === 'DRAFT') as DailyScheduleDTO[];
    if (draftSchedules.length === 0) {
      toast.info('No draft schedules to send.');
      return;
    }
    try {
      await Promise.all(draftSchedules.map((s) => DailyScheduleService(token).send(s.id)));
      setSchedules((prev) => prev.map((s) => (s && s.status === 'DRAFT' ? { ...s, status: 'SENT' } : s)));
      toast.success('Week schedule sent!');
    } catch (e: any) {
      const errors = e?.response?.data?.errors;
      if (errors?.errors) {
        toast.error(
          `Validation errors: ${errors.errors.slice(0, 2).join(', ')}${errors.errors.length > 2 ? '...' : ''}`,
        );
      } else {
        toast.error(e?.response?.data?.message || 'Failed to send schedule.');
      }
    }
  };

  const ensureScheduleAndRoom = async (
    dayIndex: number,
    roomName: string,
  ): Promise<{ schedule: DailyScheduleDTO; roomId: string }> => {
    let schedule = schedules[dayIndex];

    if (!schedule) {
      const date = weekDates[dayIndex];
      const result = await DailyScheduleService(token).create(schoolId, { date });
      schedule = result.data as DailyScheduleDTO;
    }

    let room = schedule.rooms.find((r) => r.roomName === roomName);
    if (!room) {
      const result = await DailyScheduleService(token).addRoom(schedule.id, {
        roomName,
        sortOrder: schedule.rooms.length,
      });
      room = { ...(result.data || result), entries: [] };
      schedule = { ...schedule, rooms: [...schedule.rooms, room!] };
    }

    return { schedule, roomId: room.id };
  };

  const resolveStaffStatus = (dayIndex: number, employeeId: string | null): string => {
    const date = weekDates[dayIndex];
    const emp = employeeId ? employees.find((e) => e.id === employeeId) : undefined;
    const hasPto = !!(emp && approvedPtoUserIds[date]?.includes(emp.userId));
    if (hasPto) return 'OUT_OFF';

    const target = moment(date, 'YYYY-MM-DD');
    const targetMonth = target.month();
    const targetDay = target.date();
    const isHoliday = holidays.some((h) => {
      if (!h.startDate) return false;
      const start = moment(h.startDate);
      const end = h.endDate ? moment(h.endDate) : start;
      if (h.isRecurring) {
        return (
          (targetMonth > start.month() || (targetMonth === start.month() && targetDay >= start.date())) &&
          (targetMonth < end.month() || (targetMonth === end.month() && targetDay <= end.date()))
        );
      }
      return target.isSameOrAfter(start, 'day') && target.isSameOrBefore(end, 'day');
    });
    return isHoliday ? 'OUT_OFF' : 'ON_SCHEDULE';
  };

  const handleAddEntry = async (dayIndex: number, roomName: string, employeeId: string | null) => {
    try {
      const { schedule, roomId } = await ensureScheduleAndRoom(dayIndex, roomName);
      const body: any = { staffStatus: resolveStaffStatus(dayIndex, employeeId), sortOrder: 0 };
      if (employeeId) body.employeeId = employeeId;
      const result = await DailyScheduleService(token).addEntry(schedule.id, roomId, body);
      const newEntry = result.data || result;
      setSchedules((prev) => {
        const next = [...prev];
        next[dayIndex] = {
          ...schedule,
          rooms: schedule.rooms.map((r) =>
            r.roomName === roomName ? { ...r, entries: [...(r.entries || []), newEntry] } : r,
          ),
        };
        return next;
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add entry.');
    }
  };

  const handleUpdateEntry = async (dayIndex: number, entryId: string, field: string, value: any) => {
    const schedule = schedules[dayIndex];
    if (!schedule) return;
    setSavingCell(`${dayIndex}-${entryId}`);
    try {
      const result = await DailyScheduleService(token).updateEntry(schedule.id, entryId, { [field]: value });
      const updated = result.data || result;
      setSchedules((prev) => {
        const next = [...prev];
        const s = next[dayIndex];
        if (!s) return next;
        next[dayIndex] = {
          ...s,
          rooms: s.rooms.map((r) => ({
            ...r,
            entries: r.entries.map((e) => (e.id === entryId ? { ...e, ...updated } : e)),
          })),
        };
        return next;
      });
    } catch {
      toast.error('Failed to save.');
    } finally {
      setSavingCell(null);
    }
  };

  const handleRemoveEntry = async (dayIndex: number, entryId: string) => {
    const schedule = schedules[dayIndex];
    if (!schedule) return;
    try {
      await DailyScheduleService(token).removeEntry(schedule.id, entryId);
      setSchedules((prev) => {
        const next = [...prev];
        const s = next[dayIndex];
        if (!s) return next;
        next[dayIndex] = {
          ...s,
          rooms: s.rooms.map((r) => ({
            ...r,
            entries: r.entries.filter((e) => e.id !== entryId),
          })),
        };
        return next;
      });
    } catch {
      toast.error('Failed to remove entry.');
    }
  };

  const handleAddStaffToRoom = async (roomName: string, employeeId: string) => {
    const dayIndicesWithRoom = schedules
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s !== null)
      .map(({ i }) => i);

    for (const dayIndex of dayIndicesWithRoom) {
      const schedule = schedules[dayIndex];
      if (!schedule) continue;
      const room = schedule.rooms.find((r) => r.roomName === roomName);
      if (!room) continue;
      const alreadyIn = room.entries.some((e) => e.employeeId === employeeId);
      if (alreadyIn) continue;
      await handleAddEntry(dayIndex, roomName, employeeId);
    }
  };

  const handleAddRoomForWeek = async (roomName: string) => {
    try {
      for (let dayIndex = 0; dayIndex < 5; dayIndex++) {
        await ensureScheduleAndRoom(dayIndex, roomName);
      }
      await loadWeek();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add room.');
    }
  };

  const existingSchedules = schedules.filter(Boolean) as DailyScheduleDTO[];
  const allSent = existingSchedules.length > 0 && existingSchedules.every((s) => s.status === 'SENT');

  return (
    <>
      <CommonHeader />
      <Container fluid className="daily-schedule-page mt--7">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: '0.75rem' }}>
          <div style={{ zIndex: 1 }}>
            <h2 className="page-title mb-0">Schedule Management</h2>
            <p className="page-subtitle mb-0">Create and send daily and weekly break schedules</p>
            <div
              className="d-inline-flex align-items-center p-1 mt-2"
              role="tablist"
              style={{ backgroundColor: '#e8ecf1', borderRadius: 999, gap: 0 }}
            >
              <Link
                to="/schedule"
                role="tab"
                aria-selected={false}
                style={{
                  borderRadius: 999,
                  padding: '0.45rem 1.15rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.35,
                  fontWeight: 500,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                  textDecoration: 'none',
                }}
                className="text-dark border-0"
              >
                Daily Schedule
              </Link>
              <span
                role="tab"
                aria-selected
                style={{
                  borderRadius: 999,
                  padding: '0.45rem 1.15rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.35,
                  fontWeight: 500,
                  boxShadow: '0 1px 4px rgba(94, 114, 228, 0.45)',
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                }}
                className="bg-primary text-white border-0"
              >
                Weekly Schedule
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
            <Button
              color="secondary"
              outline
              size="sm"
              className="schedule-header-btn"
              onClick={() => navigateWeek(-1)}
            >
              ‹
            </Button>
            <span className="week-range-label" style={{ zIndex: 1 }}>
              {weekLabel}
            </span>
            <Button color="secondary" outline size="sm" className="schedule-header-btn" onClick={() => navigateWeek(1)}>
              ›
            </Button>
            <Button
              color="link"
              size="sm"
              className="p-0 schedule-header-link"
              onClick={() => setWeekStart(getWeekMonday(new Date()))}
            >
              This week
            </Button>
            <Button
              color="secondary"
              outline
              size="sm"
              className="schedule-header-btn"
              onClick={handleCopyPreviousWeek}
            >
              Copy last week
            </Button>
            <Button color="primary" size="sm" disabled={allSent} onClick={handleSendWeek}>
              {allSent ? '✓ Sent' : 'Send Week'}
            </Button>
          </div>
        </div>

        {!schoolId && (
          <div className="section-card text-center py-4">
            <p className="text-muted mb-0">Please select a school to view the weekly schedule.</p>
          </div>
        )}

        {loading && !!schoolId && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && !!schoolId && (
          <WeeklyScheduleGrid
            schedules={schedules}
            weekDates={weekDates}
            employees={employees}
            savingCell={savingCell}
            approvedPtoUserIds={approvedPtoUserIds}
            holidays={holidays}
            onUpdateEntry={handleUpdateEntry}
            onRemoveEntry={handleRemoveEntry}
            onAddEntry={handleAddEntry}
            onAddStaffToRoom={handleAddStaffToRoom}
            onAddRoom={handleAddRoomForWeek}
          />
        )}
      </Container>
    </>
  );
};

export default WeeklySchedulePage;
